import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { blogService } from '../../../services/blogService';
import { projectService } from '../../../services/projectService';
import { skillService } from '../../../services/skillService';
import { experienceService } from '../../../services/experienceService';




const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const [stats, setStats] = useState({
    totalBlogs: 0,
    totalProjects: 0,
    totalSkills: 0,
    totalExperience: 0,
    newsletterSubscribers: 0
  });

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const menuItems = [
    { id: 'blogs', label: 'Blogs', icon: 'FileText', path: '/admin/dashboard/blogs', color: 'blue' },
    { id: 'projects', label: 'Projects', icon: 'Briefcase', path: '/admin/dashboard/projects', color: 'green' },
    { id: 'skills', label: 'Skills', icon: 'Code2', path: '/admin/dashboard/skills', color: 'purple' },
    { id: 'experience', label: 'Experience', icon: 'Award', path: '/admin/dashboard/experience', color: 'orange' },
    { id: 'newsletter', label: 'Newsletter', icon: 'Mail', path: '/admin/dashboard/newsletter', color: 'pink' }
  ];

  const isActivePath = (path) => location?.pathname === path;

  const fetchStats = async () => {
    try {
      const [blogs, projects, skills, experience, subscribers] = await Promise.all([
        blogService?.getAllBlogs(),
        projectService?.getAllProjects(),
        skillService?.getAllSkills(),
        experienceService?.getAllExperience(),
        blogService?.getAllSubscribers()
      ]);

      setStats({
        totalBlogs: blogs?.length || 0,
        totalProjects: projects?.length || 0,
        totalSkills: skills?.length || 0,
        totalExperience: experience?.length || 0,
        newsletterSubscribers: subscribers?.length || 0
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Portfolio</title>
      </Helmet>
      <div className="min-h-screen bg-muted/30">
        {/* Header */}
        <header className="bg-card border-b border-border sticky top-0 z-10">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="LayoutDashboard" size={20} color="var(--color-accent)" />
                </div>
                <div>
                  <h1 className="text-lg md:text-xl font-bold text-foreground">Admin Dashboard</h1>
                  <p className="text-xs text-muted-foreground">Welcome, {userProfile?.fullName}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link to="/">
                  <Button variant="outline" size="sm" iconName="Home" iconPosition="left">
                    <span className="hidden md:inline">Home</span>
                  </Button>
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="LogOut"
                  iconPosition="left"
                  onClick={handleSignOut}
                >
                  <span className="hidden md:inline">Sign Out</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12">
          <div className="mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              Content Management
            </h2>
            <p className="text-muted-foreground">
              Manage your portfolio content from one place
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {menuItems?.map((item) => (
              <Link
                key={item?.id}
                to={item?.path}
                className={`group bg-card rounded-xl border-2 p-6 transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                  isActivePath(item?.path)
                    ? 'border-accent' :'border-border hover:border-accent/50'
                }`}
              >
                <div className="flex flex-col items-center text-center">
                  <div className={`w-16 h-16 rounded-full bg-${item?.color}-500/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon name={item?.icon} size={32} color="var(--color-accent)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {item?.label}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Manage {item?.label?.toLowerCase()}
                  </p>
                  <div className="flex items-center gap-1 text-accent text-sm font-medium">
                    <span>Manage</span>
                    <Icon name="ArrowRight" size={16} />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Quick Stats */}
          <div className="mt-12">
            <h3 className="text-xl font-bold text-foreground mb-6">Quick Actions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/admin/dashboard/blogs/new">
                <Button variant="outline" fullWidth iconName="Plus" iconPosition="left">
                  New Blog Post
                </Button>
              </Link>
              <Link to="/admin/dashboard/projects/new">
                <Button variant="outline" fullWidth iconName="Plus" iconPosition="left">
                  New Project
                </Button>
              </Link>
              <Link to="/admin/dashboard/skills/new">
                <Button variant="outline" fullWidth iconName="Plus" iconPosition="left">
                  New Skill
                </Button>
              </Link>
              <Link to="/admin/dashboard/experience/new">
                <Button variant="outline" fullWidth iconName="Plus" iconPosition="left">
                  New Experience
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default AdminDashboard;
